import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { managerNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { FileWarning, ClipboardList, AlertTriangle, ArrowUpRight, Clock } from "lucide-react";

function Count({ testId, label, value, icon: Icon, to, tone }) {
  return (
    <Link to={to} data-testid={testId} className="group bg-white border border-zinc-200 rounded-md p-5 hover:border-amber-400 transition-colors">
      <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-zinc-500"><span>{label}</span><Icon className={`h-4 w-4 ${tone}`} /></div>
      <div className="font-heading font-black text-4xl text-slate-900 mt-2 num">{value}</div>
    </Link>
  );
}

export default function ManagerHome() {
  const { user, company, switchMode } = useAuth();
  const nav = useNavigate();
  const toWork = async () => { await switchMode("employee"); nav("/app"); };
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/manager/summary").then((r) => setS(r.data)).catch(() => setS({})); }, []);
  const em = { ...(company?.enabled_modules || {}), ...Object.fromEntries(Object.entries(user?.manager_modules || {}).filter(([, v]) => v === false)) };
  const modules = managerNav.filter((i) => i.to !== "/manager" && em[i.to] !== false);
  return (
    <AdminLayout title="Home">
      <PageHeader title={`Hi ${user?.name?.split(" ")[0] || ""}`} subtitle={`Manager view for ${company?.name || "your company"}`}>
        {user?.employee_id && <Button data-testid="switch-to-work-btn" onClick={toWork} className="bg-amber-500 text-black font-bold hover:bg-amber-400"><Clock className="h-4 w-4 mr-1" />My work: clock in, timesheets &amp; leave</Button>}
      </PageHeader>
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        {s?.expiring_docs != null && <Count testId="mgr-expiring" label="Docs expiring (60 days)" value={s.expiring_docs} icon={FileWarning} to="/admin/compliance" tone="text-amber-500" />}
        {s?.forms_due != null && <Count testId="mgr-forms-due" label="Checklists due this period" value={s.forms_due} icon={ClipboardList} to="/admin/forms" tone="text-slate-700" />}
        {s?.failed_open != null && <Count testId="mgr-failed-open" label="Failed items open" value={s.failed_open} icon={AlertTriangle} to="/admin/forms?tab=submissions" tone="text-rose-500" />}
      </div>
      <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Your modules</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {modules.map((m) => (
          <Link key={m.to} to={m.to} data-testid={`mgr-module-${m.to.split("/").pop()}`} className="group flex items-center gap-3 bg-white border border-zinc-200 rounded-md p-4 hover:border-amber-400 hover:-translate-y-0.5 transition-[transform,border-color]">
            <div className="h-9 w-9 rounded-md bg-slate-900 text-white flex items-center justify-center"><m.icon className="h-4 w-4" /></div>
            <span className="font-medium text-slate-900 flex-1">{m.label}</span>
            <ArrowUpRight className="h-4 w-4 text-zinc-300 group-hover:text-amber-500" />
          </Link>
        ))}
      </div>
    </AdminLayout>
  );
}
